import { useEffect, useState } from 'react';
import { PageHeader } from '../../components/layout/Header';
import { Button, Select } from '../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import Spinner from '../../components/ui/Spinner';
import { ChevronLeftIcon, ChevronRightIcon } from '../../components/icons';
import { useAppContext } from '../../contexts/AppContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { isOrgAdminRole } from '../../domain/support/guards';
import { notify } from '../../utils/toast';
import { useSupportTickets } from './hooks/useSupportTickets';
import { CreateTicketModal } from './CreateTicketModal';
import { SupportTicketDetail } from './SupportTicketDetail';
import { SupportTicketList } from './SupportTicketList';
import { TICKET_STATUS_OPTIONS } from './ticketStatusLabels';

export function SupportPage() {
    const { setCurrentPage } = useAppContext();
    const { currentOrganization } = useOrganization();
    const orgId = currentOrganization?.id;
    const canAccess = isOrgAdminRole(currentOrganization?.role);

    const {
        data,
        detail,
        isLoading,
        isDetailLoading,
        isSaving,
        error,
        refetch,
        loadDetail,
        createTicket,
        reply,
        page,
        setPage,
        status,
        setStatus,
        pageSize,
    } = useSupportTickets(orgId);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        if (currentOrganization && !canAccess) {
            setCurrentPage('dashboard');
            notify.warning('Acesso não autorizado à área de suporte.');
        }
    }, [currentOrganization, canAccess, setCurrentPage]);

    if (!canAccess) return null;

    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const canPrev = page > 1;
    const canNext = page < totalPages;
    const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, total);

    const navBtn =
        'flex h-8 w-8 items-center justify-center rounded-md border border-subtle bg-card text-secondary transition-colors hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] disabled:cursor-not-allowed disabled:opacity-40';

    const handleCreate = async (subject: string, body: string) => {
        const created = await createTicket(subject, body);
        if (created) {
            setSelectedId(created.id);
            notify.success('Ticket criado com sucesso.');
        }
        return created != null;
    };

    return (
        <div className="flex flex-col">
            <PageHeader
                title="Suporte"
                subtitle="Abra tickets e acompanhe o atendimento da equipe Adlitics"
                hideBackButton
                action={
                    <Button onClick={() => setIsCreateOpen(true)}>Novo ticket</Button>
                }
            />

            <div className="mb-4 max-w-xs">
                <Select
                    label="Status"
                    selectSize="sm"
                    value={status}
                    options={[{ value: '', label: 'Todos' }, ...TICKET_STATUS_OPTIONS]}
                    onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                    }}
                />
            </div>

            {isLoading && (
                <div className="flex items-center justify-center py-24">
                    <Spinner color="primary" size="lg" />
                </div>
            )}

            {!isLoading && error && (
                <Alert variant="destructive">
                    <AlertTitle>Erro ao carregar</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <p>{error}</p>
                        <Button variant="outline" size="sm" onClick={() => void refetch()}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && data && (
                <div className="overflow-x-auto">
                    <SupportTicketList
                        items={data.items}
                        onSelect={(id) => {
                            setSelectedId(id);
                            void loadDetail(id);
                        }}
                    />
                    {total > 0 && (
                        <div className="flex items-center justify-between border-t border-subtle px-4 py-3 text-sm text-secondary md:px-6">
                            <span>
                                {rangeStart}–{rangeEnd} de {total}
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-xs">Página {page} de {totalPages}</span>
                                <button type="button" className={navBtn} onClick={() => setPage(page - 1)} disabled={!canPrev} aria-label="Página anterior">
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>
                                <button type="button" className={navBtn} onClick={() => setPage(page + 1)} disabled={!canNext} aria-label="Próxima página">
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <CreateTicketModal
                isOpen={isCreateOpen}
                isSaving={isSaving}
                onClose={() => setIsCreateOpen(false)}
                onCreate={handleCreate}
            />

            <SupportTicketDetail
                ticketId={selectedId}
                detail={detail}
                isOpen={selectedId !== null}
                isLoading={isDetailLoading}
                isSaving={isSaving}
                onClose={() => setSelectedId(null)}
                onReply={reply}
            />
        </div>
    );
}
